import { apiRequest } from '../api/client';
import {
  conflictVersion,
  createStructuralSyncQueue,
  isMapConflict,
  type StructuralSyncResult,
} from '../api/map';
import {
  applyDeltaToSnapshot,
  diffStructure,
  emptySnapshot,
  isEmptyDelta,
  toDeltaBody,
  type StructuralDelta,
  type StructuralSnapshot,
} from './structuralDelta';
import type { DocPlan, NodeId, ProjectPlan, TagId } from '../lib/types';

/**
 * Every write this app makes to the map on the server, and the five dirty sets that decide
 * which of them is owed.
 *
 * FOUR PATHS, KEPT APART ON PURPOSE.
 *
 * 1. The BULK STRUCTURAL RESYNC (`PUT /api/map`) — a DELTA since V0.34. Position, hierarchy,
 *    colour, doc links and tag assignment, but only for what changed. It used to be the whole
 *    map every time, which was safe by construction and is exactly what made two people in
 *    the app at once fight: every save invalidated everyone else, and a stale full snapshot
 *    silently deleted work its sender had never seen.
 * 2. TRANSLATIONS, per entity and per locale. A label and a description belong to a language;
 *    the structural payload has no language, so they cannot ride it.
 * 3. AI FIELDS, per node. Deliberately NOT in the structural payload: a plain editor doing
 *    something unrelated must not send fields the server gates on AI_ADMIN or on the
 *    responsibility scope. Since V0.37 the BODY ITSELF is split too — see `AiFieldsBody`.
 * 4. NODE RESPONSIBLES (V0.37, replacing Phase 4a's single owner). Not a dirty set at all:
 *    assignment is an IMMEDIATE act via `putNodeResponsibles()` below, never a debounced map
 *    edit, and `responsibles` is not map GEOMETRY. Keeping it off `StructuralNode` is what
 *    makes "a drag can never carry an assignment" a compile-time fact rather than a habit —
 *    `structuralDelta.ts`'s `NODE_FIELD_KIND` is a `Record<keyof StructuralNode, …>`, so a
 *    field that is not on that interface cannot be classified, cannot be diffed and cannot be
 *    sent. The server agrees from its end: `nodeStructuralSchema` is `.strict()` and has no
 *    such field, and `PUT /api/nodes/:id/responsibles` is where an assignment is written.
 *
 * THE SINGLE-FLIGHT QUEUE IS A FIX, not plumbing. The vanilla `scheduleServerSync()`
 * debounced by 800 ms and then fired with no in-flight guard, so a slow PUT and an edit
 * 900 ms later put two full resyncs in the air at once. Each answers with a new
 * `dataVersion`, and whichever response landed last won the one the client kept — which
 * could be the EARLIER request's. `api/sync.ts` is the queue; the body is built at flush
 * time, because the trailing run must send state as it is when it runs.
 *
 * ── THE BASELINE, AND THE ONE RULE ABOUT IT ──────────────────────────────────────────────
 * A delta is a diff against a BASELINE snapshot, which means exactly "what the server has
 * because of us". It advances in only two ways, and never by swallowing live state:
 *
 *  - a 200, by REPLAYING the delta that was sent (`applyDeltaToSnapshot`) — between building
 *    a payload and its response the user has kept editing, and adopting the current state
 *    would quietly mark those edits as already-sent;
 *  - `rebaseStructural()`, which sets it and `dataVersion` TOGETHER, so a version can never
 *    be accepted without the state it belongs to. `loadMapFromServer()` calls it, and so does
 *    a quiet refetch.
 *
 * WHAT THIS MODULE DOES NOT KNOW is what the map looks like. `configureStructuralSync` is
 * handed a snapshot producer and a version sink by whoever owns the map state. Nothing here
 * reads a node.
 */

/* ------------------------------------------------------------------ the dirty sets */

/**
 * Node ids whose label/description changed, tag ids whose name changed, node ids whose AI
 * panel changed — and node ids whose POSITION the user meant to publish.
 *
 * EXPORTED AS THE LIVE SETS. `dirtyAiFields` in particular is read by
 * `e2e/27-aidoc-console.spec.ts` to assert that editing a prompt marked the node dirty
 * without saving it yet — which is a real property of the design (prompts are edited by the
 * paragraph and flushed before a generation, not on every keystroke) and cannot be observed
 * any other way from outside.
 *
 * `dirtyNodePositions` is the one that travels by INTENT rather than by value-diff, because a
 * value-diff cannot tell a drag from a collapse: canonical layout is a function of the
 * collapse state, so expanding a branch legitimately moves every sibling — in one person's
 * view. See `structuralDelta.ts`'s header for the full statement of the rule.
 */
export const dirtyNodeTranslations = new Set<NodeId>();
export const dirtyTagTranslations = new Set<TagId>();
export const dirtyAiFields = new Set<NodeId>();
export const dirtyNodePositions = new Set<NodeId>();

/** Mark a node's coordinates as meant for everybody. The only way a position is ever sent. */
export function markNodePosition(id: NodeId): void {
  dirtyNodePositions.add(id);
}

/** What was owed at one instant, taken out of the sets so a later edit re-marks itself. */
export interface OwedWork {
  nodeTranslations: NodeId[];
  tagTranslations: TagId[];
  aiFields: NodeId[];
  /** Reported, NOT taken — see `drainOwed()`. */
  nodePositions: NodeId[];
}

/**
 * Take everything that is owed, in one instant.
 *
 * WHY IT IS SEPARATE FROM SENDING. A full sync is three requests in sequence and the first
 * of them routinely takes half a second. If each flush drained as it was reached, "what is
 * owed" would be answered at three moments spread across seconds of network time, and an
 * edit made in between would be swept into a batch whose other members were decided before
 * it existed. Deciding once at the start is both easier to reason about and the only version
 * in which a mark's lifetime is predictable: an edit made after this call is owed by the NEXT
 * sync, always.
 *
 * POSITIONS ARE REPORTED HERE AND CLEARED ELSEWHERE, deliberately. The structural flush is
 * what actually SENDS them, and a failed send has to leave them owed; this call runs before
 * the flush and could not put them back. So `nodePositions` says what is currently marked and
 * the set itself is drained — and re-armed on failure — by `buildStructuralDelta()` below.
 */
export function drainOwed(): OwedWork {
  const owed: OwedWork = {
    nodeTranslations: [...dirtyNodeTranslations],
    tagTranslations: [...dirtyTagTranslations],
    aiFields: [...dirtyAiFields],
    nodePositions: [...dirtyNodePositions],
  };
  dirtyNodeTranslations.clear();
  dirtyTagTranslations.clear();
  dirtyAiFields.clear();
  return owed;
}

/**
 * Throw away everything that is owed, sending none of it, and say whether there was any.
 *
 * For a LOST SESSION (V0.45). Every mark here belongs to a session that no longer exists, and
 * after re-login the map is reloaded from the server — so keeping them would flush the
 * server's own values back over themselves and, worse, present them as this user's edits. The
 * honest thing is to drop them and TELL the user, which is what the return value is for: the
 * notice says "Edits made in the last moments could not be saved" only when that is true.
 *
 * Positions included, unlike `drainOwed()` — there is no flush after this to clear them.
 */
export function discardOwed(): boolean {
  const any =
    dirtyNodeTranslations.size > 0 ||
    dirtyTagTranslations.size > 0 ||
    dirtyAiFields.size > 0 ||
    dirtyNodePositions.size > 0;
  dirtyNodeTranslations.clear();
  dirtyTagTranslations.clear();
  dirtyAiFields.clear();
  dropPendingStructural();
  return any;
}

/* -------------------------------------------------------- create / delete entities */

export interface NewNodeFields {
  label: string;
  x?: number;
  y?: number;
  color?: string | null;
  side?: number;
  depth?: number;
  parentId?: NodeId | null;
  desc?: string;
  docId?: string | null;
  internalDocId?: string | null;
  courseDocId?: string | null;
  [k: string]: unknown;
}

/**
 * Adopt a version a GRANULAR route reported (V0.34).
 *
 * Every route that commits shared map content bumps `dataVersion`, which means a client goes
 * stale BY ITS OWN WRITES unless it listens: `delNode()` pushes the structural delta, calls
 * `DELETE /api/nodes/:id`, then saves — and that trailing save would carry a version the
 * delete had already superseded, 409, and send the user into a refresh for a change they made
 * themselves. Only a real number is adopted: an AI-fields-only PUT does not bump and reports
 * nothing, and the e2e write guard answers a bare `204`, which `apiRequest` gives back as
 * `undefined`.
 */
function noteGranularVersion(res: unknown): void {
  const v = (res as { dataVersion?: unknown } | undefined | null)?.dataVersion;
  if (typeof v === 'number') source?.onVersion(v);
}

export async function createNodeOnServer(locale: string, fields: NewNodeFields): Promise<NodeId> {
  const r = await apiRequest<{ id: NodeId }>('/api/nodes', {
    method: 'POST',
    body: { locale, ...fields },
  });
  noteGranularVersion(r);
  return r.id;
}

/** One node the copy route wrote, in the map payload's shape plus where it came from. */
export interface CopiedNode {
  id: NodeId;
  sourceId: NodeId;
  parentId: NodeId | null;
  [k: string]: unknown;
}

/**
 * Copy a node — or a node and its branch — under another (V0.54): the right-click menu's Paste
 * and Duplicate. The SERVER copies, because only it holds every language; the rows it wrote
 * come back in the map payload's shape, parents before children, and the version it bumped is
 * adopted here exactly as a create's is.
 */
export async function copyNodeOnServer(
  locale: string,
  sourceId: NodeId,
  body: { parentId: NodeId; branch: boolean; withPages: boolean; suffix: string | null },
): Promise<{ nodes: CopiedNode[]; tagsCopied: boolean }> {
  const r = await apiRequest<{ nodes: CopiedNode[]; tagsCopied: boolean; dataVersion?: number }>(
    `/api/nodes/${encodeURIComponent(sourceId)}/copy`,
    { method: 'POST', body: { ...body, locale } },
  );
  noteGranularVersion(r);
  // `?? []`: a response without rows (the e2e write guard answers every write with `{ok:true}`)
  // is a copy that made nothing, not a crash.
  return { nodes: r?.nodes ?? [], tagsCopied: !!r?.tagsCopied };
}

export async function createTagOnServer(
  locale: string,
  fields: Record<string, unknown>,
): Promise<TagId> {
  const r = await apiRequest<{ id: TagId }>('/api/tags', { method: 'POST', body: { locale, ...fields } });
  noteGranularVersion(r);
  return r.id;
}

/** A 404 is success: the row is gone, which is what was asked for. */
async function deleteEntity(path: string): Promise<void> {
  try {
    noteGranularVersion(await apiRequest<unknown>(path, { method: 'DELETE' }));
  } catch (err) {
    if ((err as { status?: number }).status === 404) return;
    throw err;
  }
}

export const deleteNodeOnServer = (id: NodeId): Promise<void> => deleteEntity('/api/nodes/' + id);
export const deleteTagOnServer = (id: TagId): Promise<void> => deleteEntity('/api/tags/' + id);

/* ------------------------------------------------------------------- the bulk resync */

/** What a 409 told us, and whether one of OUR changes was the thing refused. */
export interface ConflictReport {
  /**
   * The version the server says it is on — the refresh TARGET, never assigned to
   * `S.dataVersion` here. Doing that would let the trailing queue run re-send the rejected
   * change on top of the work that rejected it. Null when the body did not say.
   */
  serverVersion: number | null;
  /**
   * Which of our writes was refused, or null when we merely learned of a conflict. The only
   * value today is `'structural'`; it exists so the notice can say "your last change was not
   * saved" rather than the softer "someone else changed the map".
   */
  rejected: string | null;
}

/** What this module needs from whoever owns the map state. */
export interface StructuralSyncSource {
  /** The live map, read into a comparable snapshot. `snapshotStructure(S)`. */
  snapshot: () => StructuralSnapshot;
  /** The version the next delta must carry. */
  version: () => number;
  /** Where the version the server assigned goes. */
  onVersion: (dataVersion: number) => void;
  /** Somebody else saved first. Not a fault to report — see `pushStructuralSync`. */
  onConflict: (report: ConflictReport) => void;
  /**
   * The server refused the delta for MISSING PERMISSION BRICKS (V0.37) and named them —
   * `403 {error:'Missing permission', missing:[…]}`. Reachable only through a race (the
   * affordance sweep stops a brick-less user enqueuing the family in the first place), so the
   * app surfaces the named bricks through the route notice rather than an alert.
   */
  onForbidden: (missing: string[]) => void;
}

let queue: ReturnType<typeof createStructuralSyncQueue> | null = null;
let source: StructuralSyncSource | null = null;

/** "What the server has because of us." Never advanced by swallowing live state. */
let baseline: StructuralSnapshot = emptySnapshot();

/** The position marks one in-flight delta took, kept so a failure can put them back. */
let sentPositions: NodeId[] = [];

/**
 * Tell this module how to read the map and where to put the version that comes back.
 *
 * `onVersion` is not optional bookkeeping: the version the server assigns has to ride the
 * NEXT request, and getting it wrong is exactly what produced the 409 the queue exists to
 * prevent.
 */
export function configureStructuralSync(src: StructuralSyncSource): void {
  source = src;
  queue = createStructuralSyncQueue({
    build: buildStructuralDelta,
    commit: commitStructuralDelta,
    rollback: rollbackStructuralDelta,
  });
}

/**
 * The delta to send, or `null` for "nothing is owed".
 *
 * The position marks are TAKEN here rather than in `drainOwed()`, because this is the call
 * that can put them back: `rollbackStructuralDelta` re-arms exactly what this drained. A
 * marked id whose coordinates did not in fact move emits nothing — the mark is permission to
 * send, not an obligation — which is what keeps a Realign that changed nothing from costing a
 * request.
 */
function buildStructuralDelta(): StructuralDelta | null {
  if (!source) return null;
  const marked = [...dirtyNodePositions];
  dirtyNodePositions.clear();
  const changes = diffStructure(baseline, source.snapshot(), new Set(marked));
  if (isEmptyDelta(changes)) {
    // Nothing to send, so nothing to re-arm: every mark in `marked` described a coordinate
    // the server already holds. NO REQUEST IS MADE — that is what makes a stray `save()` free.
    sentPositions = [];
    return null;
  }
  sentPositions = marked;
  return toDeltaBody(changes, source.version());
}

function commitStructuralDelta(result: StructuralSyncResult, sent: StructuralDelta): void {
  sentPositions = [];
  source?.onVersion(result.dataVersion);
  // The ONLY way the baseline moves after a write: replay what was actually sent. A field the
  // delta did not carry keeps its baseline value, so an unmarked position stays at the
  // coordinates the server still holds rather than the ones on screen.
  baseline = applyDeltaToSnapshot(baseline, sent);
}

function rollbackStructuralDelta(): void {
  // Re-arm. The baseline did not move, so the content half of the delta will be rebuilt from
  // the diff by itself; positions travel by intent and would otherwise be lost for good.
  for (const id of sentPositions) dirtyNodePositions.add(id);
  sentPositions = [];
}

/**
 * Adopt a snapshot of the server's state as the new baseline, with the version it belongs to.
 *
 * ONE CALL for both, on purpose: a version accepted without the state it describes would make
 * the next delta a diff against a map nobody has. Called by `loadMapFromServer()` — without
 * it the first save after any load diffs against an empty baseline and re-sends the whole map
 * — and by a quiet refetch.
 */
export function rebaseStructural(snapshot: StructuralSnapshot, dataVersion: number): void {
  baseline = snapshot;
  source?.onVersion(dataVersion);
}

/**
 * Absorb a tag fold that is deliberately staying LOCAL (V0.37).
 *
 * Tag collapse is tag structure and normally syncs, but a user without `EDIT_TAGS` may still
 * fold a group for themselves — the view they have always had. Left un-absorbed, the fold
 * would sit as a permanent difference against the baseline, and every LATER delta — a drag by
 * an `EDIT_MAP`-only user, say — would carry a tags family the server must refuse, losing the
 * write that mattered over the one that was never meant to travel. Marking the baseline as
 * already holding the fold is the statement "this change is not owed", made at the moment the
 * caller decides it is not.
 */
export function absorbLocalTagFold(tagId: TagId, collapsed: boolean): void {
  const t = baseline.tags[tagId];
  if (!t || t.collapsed === collapsed) return;
  baseline = { ...baseline, tags: { ...baseline.tags, [tagId]: { ...t, collapsed } } };
}

/**
 * Forget what is owed on the structural path.
 *
 * For the conflict case: a change the server refused must be PARKED, not auto-retried on top
 * of the work that refused it. This drops the position marks and the in-flight record; the
 * content half is parked by the rebase that follows a refetch, which moves the baseline to
 * what the server actually has.
 */
export function dropPendingStructural(): void {
  dirtyNodePositions.clear();
  sentPositions = [];
}

/** Is the structural path mid-write, or about to re-run? A refresh must not cut across it. */
export const structuralSyncBusy = (): boolean => !!queue && (queue.inFlight || queue.pending);

/**
 * Send the structural delta, coalescing with anything already in flight.
 *
 * Resolves when a run that started at or after this call has settled — so `await`ing it
 * means "my state has been sent", which is what `delNode` needs before it removes the node
 * whose children it has just reparented.
 */
export async function pushStructuralSync(): Promise<void> {
  if (!queue) return;
  try {
    await queue.request();
  } catch (err) {
    // Somebody else saved first. Not a fault to report — the only honest answer is to take
    // their version rather than overwrite it, and that decision belongs to the app.
    if (isMapConflict(err)) {
      source?.onConflict({ serverVersion: conflictVersion(err), rejected: 'structural' });
      return;
    }
    // A brick check refused the whole delta and named what was missing (V0.37). Also not a
    // fault to throw over: the app parks the refused change and says which permission it was.
    const missing = missingBricksOf(err);
    if (missing) {
      source?.onForbidden(missing);
      return;
    }
    throw err;
  }
}

/** The `missing` list of a V0.37 permission refusal, or null for any other failure. */
function missingBricksOf(err: unknown): string[] | null {
  const e = err as { status?: number; body?: { error?: string; missing?: unknown } } | null;
  if (!e || e.status !== 403) return null;
  const missing = e.body?.missing;
  if (!Array.isArray(missing) || !missing.every((m) => typeof m === 'string')) return null;
  return missing as string[];
}

/* ---------------------------------------------------------- translations & AI fields */

export interface NodeTranslation {
  label: string;
  desc: string;
}

/**
 * Flush the two translation sets.
 *
 * `owed` lets a full sync decide once, up front, what all three paths are sending (see
 * `drainOwed`). Called without it — which is what a standalone flush does — it takes whatever
 * is owed right now.
 *
 * `onNodeSaved` hands the response back, because one rename can change more than the label:
 * a node still carrying the placeholder slug the app invented for it is re-slugged from its
 * first real name (V0.45, server-side), and the client has to adopt that or the address bar
 * goes on showing the old one until a reload. See `adoptNodeSlug` in actions.ts.
 */
export async function flushDirtyTranslations(
  locale: string,
  nodeOf: (id: NodeId) => NodeTranslation | null,
  tagNameOf: (id: TagId) => string | null,
  owed?: OwedWork,
  onNodeSaved?: (id: NodeId, response: unknown) => void,
): Promise<void> {
  const nodeIds = owed ? owed.nodeTranslations : [...dirtyNodeTranslations];
  const tagIds = owed ? owed.tagTranslations : [...dirtyTagTranslations];
  if (!owed) {
    dirtyNodeTranslations.clear();
    dirtyTagTranslations.clear();
  }

  for (const id of nodeIds) {
    const t = nodeOf(id);
    if (!t) continue;
    try {
      const res = await apiRequest<unknown>(`/api/nodes/${id}/translations/${locale}`, {
        method: 'PUT',
        body: t,
      });
      noteGranularVersion(res);
      onNodeSaved?.(id, res);
    } catch (e) {
      console.error('Failed to sync translation for node', id, e);
    }
  }
  for (const id of tagIds) {
    const name = tagNameOf(id);
    if (name === null) continue;
    try {
      noteGranularVersion(
        await apiRequest<unknown>(`/api/tags/${id}/translations/${locale}`, { method: 'PUT', body: { name } }),
      );
    } catch (e) {
      console.error('Failed to sync translation for tag', id, e);
    }
  }
}

/**
 * The AI-fields body, split the way `PUT /api/nodes/:id` gates it since V0.37:
 * `aiPrompt` is the RESPONSIBLE family — and since V0.38 it is that family's ONLY field
 * (`docStatus` died with the workflow unification; sending it is a 400 now). Everything else
 * is generation MECHANICS and stays AI_ADMIN. The mechanics keys are optional because the
 * body builder OMITS them for a non-AI_ADMIN — sending them, even unchanged, would trip the
 * admin-family gate and 403 the whole write.
 */
export interface AiFieldsBody {
  aiPrompt: string | null;
  liveProbeEnabled?: boolean;
  appTargetId?: string | null;
  appPath?: string | null;
  [k: string]: unknown;
}

/**
 * Flush the AI fields, one `PUT /api/nodes/:id` per node.
 *
 * A failure is logged and skipped rather than retried: these are edited by the paragraph and
 * flushed again before the next generation, so the next flush carries the current value
 * anyway. Retrying here would send a value that may already be stale.
 */
export async function flushDirtyAiFields(
  bodyOf: (id: NodeId) => AiFieldsBody | null,
  owed?: OwedWork,
): Promise<void> {
  const ids = owed ? owed.aiFields : [...dirtyAiFields];
  if (!owed) dirtyAiFields.clear();
  for (const id of ids) {
    const body = bodyOf(id);
    if (!body) continue;
    try {
      // An AI-fields-only body does not move the shared map, so the server reports no
      // version here — `noteGranularVersion` is a no-op unless something structural rode
      // along, which on this path it never does.
      noteGranularVersion(await apiRequest<unknown>('/api/nodes/' + id, { method: 'PUT', body }));
    } catch (e) {
      console.error('Failed to sync AI fields for node', id, e);
    }
  }
}

/* -------------------------------------------------------- node responsibles (V0.37) */

/**
 * Replace one node's responsibles — `PUT /api/nodes/:id/responsibles`, full replace, awaited
 * by its caller rather than debounced. Assignment is an immediate act with its own gate
 * (`mayAssignNode`, server-side), never a map edit: there is no dirty set, no undo leg, and
 * nothing about it in the structural delta.
 *
 * IT ADOPTS THE VERSION IT IS GIVEN. Unlike the AI-fields PUT next door, this route DOES bump
 * `dataVersion` — node responsibles ride the `/api/map` payload every participant renders —
 * and without `noteGranularVersion` this client would go stale by its own write, 409 on its
 * next structural save, and be sent into a refresh for a change the user just made.
 *
 * Errors are the CALLER's: a 400 here NAMES an ineligible assignee (or, V0.37.1, an
 * ineligible GROUP) and the popup wants to show that sentence, so nothing is swallowed here.
 */
export async function putNodeResponsibles(
  nodeId: NodeId,
  userIds: string[],
  groupIds: string[] = [],
): Promise<{ responsibles: string[]; responsibleGroups: string[] }> {
  const res = await apiRequest<{ responsibles?: string[]; responsibleGroups?: string[] }>(
    `/api/nodes/${nodeId}/responsibles`,
    { method: 'PUT', body: { userIds, groupIds } },
  );
  noteGranularVersion(res);
  // The e2e write guard answers `{ok:true}` with no lists; the sent sets are then the truth.
  return {
    responsibles: res?.responsibles ?? [...userIds].sort(),
    responsibleGroups: res?.responsibleGroups ?? [...groupIds].sort(),
  };
}

/**
 * Replace one doc page's responsibles for ONE locale (V0.38) — the doc-shaped sibling of
 * `putNodeResponsibles()`, fired from the WorkflowControl popover's picker. Full replace of
 * both families (the popover always draws both halves, so it always sends both).
 *
 * DELIBERATELY NO VERSION TO ADOPT: doc responsibles are not on the map payload and this
 * route does not bump `dataVersion` — they reach other clients on the workflows fetch
 * lifecycle, the documented asymmetry (plan trap #6). `noteGranularVersion` still runs in
 * case that ever changes; today it is a no-op here. Errors are the CALLER's, as on the node
 * PUT: the 400 for an ineligible assignee names them, and the popover wants that sentence.
 */
export async function putDocResponsibles(
  docId: string,
  locale: string,
  userIds: string[],
  groupIds: string[],
): Promise<{ responsibles: string[]; responsibleGroups: string[] }> {
  const res = await apiRequest<{ responsibles?: string[]; responsibleGroups?: string[] }>(
    `/api/docs/${docId}/responsibles`,
    { method: 'PUT', body: { locale, userIds, groupIds } },
  );
  noteGranularVersion(res);
  return {
    responsibles: res?.responsibles ?? [...userIds].sort(),
    responsibleGroups: res?.responsibleGroups ?? [...groupIds].sort(),
  };
}

/**
 * Translate ONE page into ONE language — `POST /api/docs/revisions/:id/translate`. The ONE
 * wire call for translation (V0.41): the AI console's bulk loop and the doc bar's per-page
 * "Translate into {locale}" button both go through here, so there is a single request shape
 * and a single place the route is named. Server-gated (AI_ADMIN ∨ the language's edit scope,
 * plus the `translate` capability); callers own their own follow-up (the console reports
 * progress, the doc bar re-opens the page).
 */
export async function translateDoc(docId: string, targetLocale: string): Promise<void> {
  await apiRequest(`/api/docs/revisions/${encodeURIComponent(docId)}/translate`, {
    method: 'POST',
    body: { targetLocale },
  });
}

/* ---------------------------------------------------------- the documentation plan (V0.38) */

/**
 * Write one node's documentation plan — `PUT /api/nodes/:id/doc-plan`, an immediate act with
 * its own gate (`mayAssignNode`), kept off the structural delta exactly as responsibles are.
 * IT ADOPTS THE VERSION: the plan rides the `/api/map` payload participants render, so the
 * route bumps `dataVersion` and this client must not go stale by its own write.
 */
export async function putNodeDocPlan(
  nodeId: NodeId,
  plan: DocPlan | null,
): Promise<{ expectedDocs: DocPlan | null }> {
  const res = await apiRequest<{ expectedDocs?: DocPlan | null; dataVersion?: number }>(
    `/api/nodes/${nodeId}/doc-plan`,
    { method: 'PUT', body: { plan } },
  );
  noteGranularVersion(res);
  // The e2e write guard answers `{ok:true}` with no plan; the sent value is then the truth.
  return { expectedDocs: res && 'expectedDocs' in res ? (res.expectedDocs ?? null) : plan };
}

/* ------------------------------------------------------------------- the project mark (V0.50.3) */

/**
 * Mark a node as a project, change its dates, or unmark it — `PUT /api/nodes/:id/project`,
 * `putNodeDocPlan`'s contract exactly: an immediate act on its own endpoint (gated on the two
 * to-do permissions), kept off the structural delta, and IT ADOPTS THE VERSION — since V0.50.3
 * the mark rides the `/api/map` payload for the map's flag, so the route bumps `dataVersion` and
 * this client must not go stale by its own write. `null` unmarks; otherwise the WHOLE plan
 * travels, both keys, because the server takes a total write.
 */
export async function putNodeProject(
  nodeId: NodeId,
  plan: ProjectPlan | null,
): Promise<{ project: ProjectPlan | null }> {
  const res = await apiRequest<{ project?: ProjectPlan | null; dataVersion?: number }>(
    `/api/nodes/${encodeURIComponent(nodeId)}/project`,
    { method: 'PUT', body: { project: plan } },
  );
  noteGranularVersion(res);
  // The e2e write guard answers `{ok:true}` with no plan; the sent value is then the truth.
  return { project: res && 'project' in res ? (res.project ?? null) : plan };
}

/* ------------------------------------------- the documentation location in an app (V0.46) */

/**
 * Read one node's `docPath` — "where this documentation lives inside the real application" —
 * with the effective `appPath` beside it, and which ancestor supplied that.
 *
 * `EDIT_MAP`-gated, and NOT on `/api/map`: `docPath` drives the widget's public URL resolution
 * and `appPath` is AI_ADMIN's on the map payload, so the two are legible together only here,
 * per node, for somebody who may edit the map. The wire contract for the widget forbids either
 * field outright, which is why there is no other way to see them.
 *
 * NO VERSION TO ADOPT: neither route bumps `dataVersion`, because neither column rides the map
 * payload every participant renders.
 */
export async function getNodeDocPath(nodeId: NodeId): Promise<{
  docPath: string | null;
  appPath: string | null;
  effectiveAppPath: string | null;
  effectiveAppPathNodeId: NodeId | null;
}> {
  const res = await apiRequest<{
    docPath?: string | null;
    appPath?: string | null;
    effectiveAppPath?: string | null;
    effectiveAppPathNodeId?: string | null;
  }>(`/api/nodes/${nodeId}/doc-path`);
  return {
    docPath: res?.docPath ?? null,
    appPath: res?.appPath ?? null,
    effectiveAppPath: res?.effectiveAppPath ?? null,
    effectiveAppPathNodeId: res?.effectiveAppPathNodeId ?? null,
  };
}

/** Write it. Blank is UNSET, which is what the resolver reads it as too. */
export async function putNodeDocPath(nodeId: NodeId, docPath: string | null): Promise<void> {
  await apiRequest(`/api/nodes/${nodeId}/doc-path`, { method: 'PUT', body: { docPath } });
}
